import { useEffect, useRef, useState } from "react";
import api, { fmtMoney, formatApiError, detailFromAxiosError, tipoLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, FileDown, FileSearch, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;

const CANCEL_SOURCE_LABELS = {
  folder: "Carpeta de carga (ruta de cancelados)",
  sat_metadata: "Metadatos SAT (.txt)",
  sat_verify: "Verificación SAT",
  manual: "Marcado manual en visor",
};

async function openCfdiPdfInNewTab(cfdiId) {
  const popup = window.open("", "_blank");
  if (!popup) {
    toast.error("Habilita ventanas emergentes para ver el PDF");
    return;
  }
  try {
    const { data } = await api.get(`/cfdi/${cfdiId}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    popup.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 180000);
  } catch (e) {
    try { popup.close(); } catch {}
    const msg = (await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "No se pudo cargar el PDF";
    toast.error(msg);
  }
}

function Field({ label, value, mono, right, bold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${right ? "text-right" : ""} ${bold ? "font-bold" : ""} text-slate-800 mt-0.5 break-all`}>{value || "—"}</div>
    </div>
  );
}

export default function CfdiConfrontaDetailDialog({ detail, onClose, onAfterMutate }) {
  const [marking, setMarking] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [satResult, setSatResult] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [localItem, setLocalItem] = useState(null);
  const pdfObjectUrlRef = useRef(null);

  useEffect(() => {
    setLocalItem(detail?.item || null);
  }, [detail]);

  const item = localItem;
  const cfdiRef = item?.id || item?.uuid || "";

  useEffect(() => {
    let cancelled = false;

    if (!detail || !cfdiRef) {
      if (pdfObjectUrlRef.current) {
        URL.revokeObjectURL(pdfObjectUrlRef.current);
        pdfObjectUrlRef.current = null;
      }
      setPdfBlobUrl("");
      setPdfError("");
      setPdfBusy(false);
      return;
    }

    if (pdfObjectUrlRef.current) {
      URL.revokeObjectURL(pdfObjectUrlRef.current);
      pdfObjectUrlRef.current = null;
    }

    setPdfBusy(true);
    setPdfError("");

    api.get(`/cfdi/${cfdiRef}/pdf`, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        const url = URL.createObjectURL(data);
        pdfObjectUrlRef.current = url;
        setPdfBlobUrl(url);
      })
      .catch(async (e) => {
        if (cancelled) return;
        setPdfBlobUrl("");
        const msg =
          (await detailFromAxiosError(e)) ||
          formatApiError(e.response?.data?.detail) ||
          "No se pudo cargar el PDF";
        setPdfError(msg);
      })
      .finally(() => {
        if (cancelled) return;
        setPdfBusy(false);
      });

    return () => { cancelled = true; };
  }, [detail, cfdiRef]);

  useEffect(() => {
    if (!detail) {
      setSatResult(null);
      setMarking(null);
      setVerifying(false);
    }
  }, [detail]);

  useEffect(() => {
    return () => {
      if (pdfObjectUrlRef.current) {
        URL.revokeObjectURL(pdfObjectUrlRef.current);
        pdfObjectUrlRef.current = null;
      }
    };
  }, []);

  if (!detail) return null;

  const { observation, uuid: missingUuid } = detail;
  const c = item;
  const folioStr = c ? ([c.serie, c.folio].filter(Boolean).join("-") || "—") : "—";
  const obsColors = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    red: "bg-red-50 border-red-200 text-red-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };

  const pagosRecibidos = c?.tipo_comprobante !== "P" ? (c?.pagos_recibidos || []) : [];
  const totalPagado = pagosRecibidos
    .filter((p) => (p.pago_estado || "Vigente") !== "Cancelado")
    .reduce((s, p) => s + (Number(p.imp_pagado) || 0), 0);
  const totalCfdi = Number(c?.total) || 0;
  const overpayDelta = round2(totalPagado - totalCfdi);
  const isOverpay = totalCfdi > 0 && overpayDelta > 0.01;

  const handleDownloadPdf = () => {
    if (pdfBusy || !!pdfError || !pdfBlobUrl) return;
    const uuidSuffix = (c?.uuid || "").slice(0, 8);
    const filename = `CFDI_${uuidSuffix || cfdiRef || "cfdi"}.pdf`;
    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const markPagoCancelado = async (pagoUuid) => {
    if (!pagoUuid) return;
    if (!window.confirm(`¿Marcar el complemento de pago ${pagoUuid.slice(0, 8)}… como CANCELADO?\n\nEsto excluirá ese pago de la confronta y de los reportes. Asegúrate de haber verificado en SAT que está cancelado.`)) return;
    setMarking(pagoUuid);
    try {
      const { data } = await api.post("/cfdi/bulk-estado", {
        uuids: [pagoUuid],
        estado: "Cancelado",
        client_id: c?.client_id || undefined,
      });
      if (data?.modified > 0) {
        toast.success("Complemento marcado como cancelado. La confronta se actualizará al recargar.");
        if (onAfterMutate) await onAfterMutate();
        onClose();
      } else {
        toast.warning("No se encontró el complemento para actualizar.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cancelar el complemento.");
    } finally {
      setMarking(null);
    }
  };

  const verifyEnSAT = async () => {
    if (!c?.uuid) {
      toast.error("No hay UUID válido para verificar.");
      return;
    }
    const uuidsToCheck = [c.uuid, ...pagosRecibidos.map((p) => p.pago_uuid)]
      .map((u) => String(u || "").trim().toUpperCase())
      .filter((u, idx, arr) => UUID_RE.test(u) && arr.indexOf(u) === idx);
    if (!uuidsToCheck.length) {
      toast.error("No hay UUID válido para verificar en SAT.");
      return;
    }
    setVerifying(true);
    setSatResult(null);
    try {
      const payload = {
        uuids: uuidsToCheck,
        auto_update: true,
      };
      if (c.client_id) payload.client_id = c.client_id;
      const { data } = await api.post("/cfdi/sat-verify", payload, {
        timeout: 60000,
        headers: { "Content-Type": "application/json" },
      });
      setSatResult(data);
      const cambios = data?.actualizados || 0;
      if (c.client_id && c.uuid) {
        try {
          const { data: refreshed } = await api.get("/cfdi/by-uuid", {
            params: { client_id: c.client_id, uuid: c.uuid },
          });
          setLocalItem(refreshed);
        } catch {
          // confronta puede seguir; el SAT ya respondió
        }
      }
      if (cambios > 0) {
        toast.success(`SAT verificó ${data.verificados} CFDI · ${cambios} actualizado(s) en la copia local.`);
        if (onAfterMutate) await onAfterMutate();
      } else if ((data?.cancelados || 0) > 0) {
        toast.info(`SAT reporta ${data.cancelados} cancelado(s) (coincide con la copia local).`);
      } else {
        toast.success(`SAT verificó ${data.verificados} CFDI · estado vigente confirmado.`);
        if (onAfterMutate) await onAfterMutate();
      }
    } catch (e) {
      const detailMsg = formatApiError(e.response?.data?.detail);
      toast.error(detailMsg || e.message || "No se pudo verificar en SAT.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={!!detail} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={`rounded-sm max-h-[90vh] overflow-hidden flex flex-col ${c ? "max-w-[95vw] w-[1200px]" : "max-w-3xl overflow-y-auto"}`}
        data-testid="cfdi-detail-dialog"
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between pr-8 gap-3 flex-wrap">
            <DialogTitle style={{ fontFamily: "'Chivo'" }}>
              {c ? `Detalle CFDI · ${tipoLabel(c.tipo_comprobante)}` : "CFDI no cargado en el sistema"}
            </DialogTitle>
            {c && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={pdfBusy || !!pdfError || !pdfBlobUrl}
                className="h-8 text-xs"
              >
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> Descargar PDF
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className={c ? "flex-1 overflow-hidden flex gap-6 p-1 min-h-0" : ""}>
          <div className={c ? "flex-1 overflow-y-auto space-y-4 pr-2" : "space-y-4"}>
            {observation && (
              <div className={`border rounded-sm p-3 text-xs ${obsColors[observation.color] || obsColors.slate}`} data-testid="cfdi-obs">
                <div className="font-semibold mb-1">Observación: {observation.titulo}</div>
                <div className="whitespace-pre-line opacity-90">{observation.detalle}</div>
              </div>
            )}

            {!c && missingUuid && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-xs text-slate-700">
                <div className="font-semibold mb-1">UUID buscado:</div>
                <div className="font-mono text-[11px] break-all">{missingUuid}</div>
                <div className="mt-2 text-slate-500">Este CFDI no está cargado en el sistema (o pertenece a otro cliente). Carga el XML correspondiente desde Carga de información.</div>
              </div>
            )}

            {c && (
              <div className="space-y-4 text-sm">
                {c.estado === "Cancelado" && (
                  <div className="rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950" data-testid="cfdi-cancel-local-banner">
                    <div className="font-semibold mb-1">Cancelado en la copia local del visor</div>
                    <p className="m-0 leading-relaxed">
                      El PDF y la confronta usan el campo <strong>estado</strong> guardado en MongoDB, no una consulta en vivo al portal SAT.
                      {c.cancel_source ? (
                        <> Origen registrado: <strong>{CANCEL_SOURCE_LABELS[c.cancel_source] || c.cancel_source}</strong>.</>
                      ) : (
                        <> No hay origen registrado del estatus.</>
                      )}
                      {c.source_path ? (
                        <> Ruta de carga: <span className="font-mono text-[10px] break-all">{c.source_path}</span>.</>
                      ) : null}
                      {" "}Pulse <strong>Verificar en SAT</strong> para contrastar con el portal oficial.
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                  <div className="text-[11px] text-slate-600">
                    {c.verificado_sat_at ? (
                      <>Verificado SAT: <span className="font-mono">{(c.verificado_sat_at || "").replace("T", " ").slice(0, 16)}</span></>
                    ) : (
                      <>Aún no verificado en SAT</>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={verifyEnSAT}
                    disabled={verifying}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded-sm border border-[#0F2D5C] text-[#0F2D5C] hover:bg-[#0F2D5C] hover:text-white disabled:opacity-50"
                    data-testid="verify-sat-btn"
                  >
                    {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                    Verificar en SAT
                  </button>
                </div>

                {satResult && (
                  <div className="border border-slate-200 rounded-sm bg-white" data-testid="sat-result-panel">
                    <div className="px-3 py-2 bg-slate-100 text-[10px] uppercase tracking-wider text-slate-700 font-semibold">
                      Respuesta del SAT — {satResult.verificados} verificado(s) · {satResult.vigentes} vigente(s) · {satResult.cancelados} cancelado(s) · {satResult.no_encontrados || 0} no encontrado(s) · {satResult.errores} error(es)
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">UUID</th>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">Estado SAT</th>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">Es cancelable</th>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">Estatus cancelación</th>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">BD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(satResult.resultados || []).map((r, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 font-mono text-[10px]">{r.uuid}</td>
                            <td className={`px-3 py-1.5 font-semibold ${r.estado_sat === "Cancelado" ? "text-rose-700" : r.estado_sat === "Vigente" ? "text-emerald-700" : "text-slate-600"}`}>{r.estado_sat || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-700">{r.es_cancelable || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-700">{r.estatus_cancelacion || "—"}</td>
                            <td className="px-3 py-1.5 text-[10px]">
                              {r.actualizado ? (
                                <span className="text-amber-700 font-semibold">Actualizado: {r.estado_anterior} → {r.estado_sat}</span>
                              ) : r.error ? (
                                <span className="text-rose-700">{r.error}</span>
                              ) : (
                                <span className="text-slate-500">Sin cambios</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4 text-xs">
                  <Field label="UUID" value={c.uuid} mono />
                  <Field label="Serie / Folio" value={folioStr} mono />
                  <Field label="Estado" value={c.estado || "Vigente"} />
                  <Field label="Fecha emisión" value={(c.fecha || "").replace("T", " ").slice(0, 16)} mono />
                  <Field label="Método pago" value={c.metodo_pago || "—"} mono />
                  <Field label="Forma pago" value={c.forma_pago || "—"} mono />
                  <Field label="RFC Emisor" value={`${c.rfc_emisor || ""} · ${c.nombre_emisor || ""}`} mono />
                  <Field label="RFC Receptor" value={`${c.rfc_receptor || ""} · ${c.nombre_receptor || ""}`} mono />
                  <Field label="Versión" value={c.version} mono />
                  <Field label="Subtotal" value={fmtMoney(c.subtotal)} right />
                  <Field label="IVA trasladado" value={fmtMoney(c.iva_trasladado)} right />
                  <Field label="Total" value={fmtMoney(c.total)} right bold />
                </div>

                {c.tipo_comprobante === "P" && (c.pagos?.length > 0) && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Complemento de Pagos</div>
                    {(c.pagos || []).map((p, i) => (
                      <div key={i} className="border border-slate-200 rounded-sm mb-2">
                        <div className="bg-slate-50 px-3 py-2 grid grid-cols-3 gap-3 text-xs">
                          <Field label="Fecha pago" value={(p.fecha_pago || "").replace("T", " ").slice(0, 16)} mono />
                          <Field label="Monto" value={fmtMoney(p.monto)} right />
                          <Field label="Forma" value={p.forma_pago || "—"} mono />
                        </div>
                        <table className="w-full text-xs">
                          <thead className="bg-white">
                            <tr>
                              <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Factura relacionada (UUID)</th>
                              <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Serie/Folio</th>
                              <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Parc.</th>
                              <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pagado</th>
                              <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Saldo insoluto</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(p.doctos || []).map((d, j) => (
                              <tr key={j} className="border-t border-slate-100">
                                <td className="px-3 py-1.5 font-mono text-[10px]">{d.uuid}</td>
                                <td className="px-3 py-1.5 font-mono">{[d.serie, d.folio].filter(Boolean).join("-") || "—"}</td>
                                <td className="px-3 py-1.5 text-right num">{d.parcialidad}</td>
                                <td className="px-3 py-1.5 text-right num">{fmtMoney(d.imp_pagado)}</td>
                                <td className="px-3 py-1.5 text-right num">{fmtMoney(d.imp_saldo_insoluto)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}

                {c.tipo_comprobante !== "P" && c.pagos_recibidos?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pagos recibidos / aplicados a esta factura</div>
                      <div className="text-[11px] text-slate-600">
                        Suma pagada: <span className={`font-semibold ${isOverpay ? "text-red-700" : "text-slate-800"}`}>{fmtMoney(totalPagado)}</span> / Total CFDI: <span className="font-semibold">{fmtMoney(totalCfdi)}</span>
                      </div>
                    </div>

                    {isOverpay && (
                      <div className="mb-2 border border-red-300 bg-red-50 text-red-900 rounded-sm p-3 text-xs flex items-start gap-2" data-testid="overpay-warning">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
                        <div className="space-y-1">
                          <div className="font-semibold">Sobre-pago detectado</div>
                          <div>
                            Los complementos suman <strong>{fmtMoney(totalPagado)}</strong> contra un total del CFDI de <strong>{fmtMoney(totalCfdi)}</strong> ({fmtMoney(overpayDelta)} de exceso).
                            Verifica en el portal del SAT cuál UUID está cancelado y márcalo aquí con el botón <strong>Cancelar</strong>.
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="border border-slate-200 rounded-sm overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">UUID Pago</th>
                            <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">Fecha pago</th>
                            <th className="px-3 py-1.5 text-right text-[10px] uppercase text-slate-500 font-semibold">Parc.</th>
                            <th className="px-3 py-1.5 text-right text-[10px] uppercase text-slate-500 font-semibold">Importe pagado</th>
                            <th className="px-3 py-1.5 text-right text-[10px] uppercase text-slate-500 font-semibold">Saldo insoluto</th>
                            <th className="px-3 py-1.5 text-center text-[10px] uppercase text-slate-500 font-semibold">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.pagos_recibidos.map((p, i) => {
                            const isCanc = (p.pago_estado || "Vigente") === "Cancelado";
                            return (
                              <tr key={i} className={`border-t border-slate-100 ${isCanc ? "bg-rose-50/40 line-through text-slate-400" : ""}`}>
                                <td className="px-3 py-1.5 font-mono text-[10px]">{p.pago_uuid}</td>
                                <td className="px-3 py-1.5 font-mono">{(p.fecha_pago || "").replace("T", " ").slice(0, 16)}</td>
                                <td className="px-3 py-1.5 text-right num">{p.parcialidad}</td>
                                <td className="px-3 py-1.5 text-right num">{fmtMoney(p.imp_pagado)}</td>
                                <td className="px-3 py-1.5 text-right num">{fmtMoney(p.imp_saldo_insoluto)}</td>
                                <td className="px-3 py-1.5 text-center no-underline">
                                  {isCanc ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-rose-700 font-semibold">
                                      <XCircle className="w-3 h-3" /> Cancelado
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => markPagoCancelado(p.pago_uuid)}
                                      disabled={marking === p.pago_uuid}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                                      data-testid={`mark-pago-cancel-${i}`}
                                    >
                                      {marking === p.pago_uuid ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                                      Cancelar
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {c && (
            <div className="w-[550px] flex-shrink-0 border-l border-slate-200 pl-6 flex flex-col min-h-[420px]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                <FileSearch className="w-3 h-3" /> Representación impresa (vista previa)
              </div>
              <div className="flex-1 bg-slate-100 rounded-sm border border-slate-200 overflow-hidden relative min-h-[360px]">
                {pdfBusy && (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-600 bg-slate-100/80 z-10">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cargando PDF...
                  </div>
                )}

                {!pdfBusy && pdfError && (
                  <div className="absolute inset-0 overflow-auto p-4 text-xs text-rose-700 bg-white z-10">
                    <div className="font-semibold mb-2">No se pudo cargar el PDF</div>
                    <div className="whitespace-pre-wrap break-all">{pdfError}</div>
                    <div className="mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openCfdiPdfInNewTab(cfdiRef)}
                        className="h-7 text-xs"
                      >
                        Abrir en pestaña
                      </Button>
                    </div>
                  </div>
                )}

                {!pdfBusy && !pdfError && pdfBlobUrl && (
                  <iframe
                    src={`${pdfBlobUrl}#toolbar=0&navpanes=0&view=FitH`}
                    className="w-full h-full border-0"
                    title="Vista previa CFDI"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
